import classNames from 'classnames'
import {
  useCallback,
  useMemo,
  type CSSProperties,
  type FC,
  type ReactElement,
  type Ref,
} from 'react'
import {
  ReactGridLayout,
  noCompactor,
  useContainerWidth,
  type EventCallback,
  type ResizeHandleAxis,
} from 'react-grid-layout'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

import { useIsRTL } from '@context/SessionSettingsContext'

import { IconButton } from '@ui/components/Button'
import { ResizeIcon } from '@ui/icons/Resize.icon'

import { WidgetType } from '../types'
import type { DashboardGridChange, Widget, WidgetLayout } from '../types'
import {
  DASHBOARD_GRID_BACKGROUND_SIZE,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GRID_CONTAINER_PADDING,
  DASHBOARD_GRID_MARGIN,
  DASHBOARD_GRID_MAX_WIDTH,
  DASHBOARD_GRID_STEP,
  getDashboardGridMetrics,
} from '../utils/dashboardGrid.constants'
import { dashboardGridCompactor, getMaxResizeWidth } from '../utils/dashboardGrid.utils'
import { getWidgetConstraints } from '../widgetLayouts'
import { WidgetContainer } from './WidgetContainer'

import styles from './DashboardGrid.module.scss'

type DashboardGridProps = {
  widgets: Widget[]
} & (
  | {
      isEditMode: true
      isMobile?: never
      onLayoutChange: (change: DashboardGridChange) => void
      onRemoveWidget: (type: WidgetType) => void
    }
  | {
      isEditMode?: false
      isMobile: boolean
      onLayoutChange?: never
      onRemoveWidget?: never
    }
)

export const DashboardGrid: FC<DashboardGridProps> = (props) => {
  const isRTL = useIsRTL()
  const {
    width: containerWidth,
    containerRef,
    mounted,
  } = useContainerWidth({
    measureBeforeMount: true,
  })
  const isEditMode = props.isEditMode === true

  return (
    <div
      ref={containerRef}
      className={classNames(styles.dashboardGrid, {
        [styles.showGridLines]: isEditMode,
        [styles.rtl]: isRTL,
      })}
      style={
        {
          '--dashboard-grid-background-size': DASHBOARD_GRID_BACKGROUND_SIZE + 'px',
        } as CSSProperties
      }
    >
      {mounted &&
        (props.isEditMode ? (
          <EditableDashboardGrid
            widgets={props.widgets}
            isRTL={isRTL}
            containerWidth={containerWidth}
            onLayoutChange={props.onLayoutChange}
            onRemoveWidget={props.onRemoveWidget}
          />
        ) : (
          <ReadOnlyDashboardGrid
            widgets={props.widgets}
            isRTL={isRTL}
            isMobile={props.isMobile}
            containerWidth={containerWidth}
          />
        ))}
    </div>
  )
}

interface EditableDashboardGridProps {
  widgets: Widget[]
  isRTL: boolean
  containerWidth: number
  onLayoutChange: (change: DashboardGridChange) => void
  onRemoveWidget: (type: WidgetType) => void
}

const EditableDashboardGrid: FC<EditableDashboardGridProps> = ({
  widgets,
  isRTL,
  containerWidth,
  onLayoutChange,
  onRemoveWidget,
}) => {
  const { columns, width } = getDashboardGridMetrics(containerWidth)
  const resizeHandleAxis: ResizeHandleAxis = isRTL ? 'sw' : 'se'
  const layout = useMemo(() => {
    const constrainedLayout = widgets
      .map(getEditableWidgetLayout)
      .filter((item): item is WidgetLayout => item !== null)
    const resizeDirection = isRTL ? 'left' : 'right'

    return constrainedLayout.map((item) => ({
      ...item,
      maxW: getMaxResizeWidth(item, constrainedLayout, resizeDirection),
    }))
  }, [isRTL, widgets])

  const handleDragStop = useCallback<EventCallback>(
    (nextLayout, _oldItem, changedItem) => {
      if (changedItem) {
        onLayoutChange({ layout: nextLayout, changedItem, interaction: 'drag' })
      }
    },
    [onLayoutChange]
  )

  const handleResizeStop = useCallback<EventCallback>(
    (nextLayout, _oldItem, changedItem) => {
      if (changedItem) {
        onLayoutChange({ layout: nextLayout, changedItem, interaction: 'resize' })
      }
    },
    [onLayoutChange]
  )

  return (
    <ReactGridLayout
      width={width}
      className={styles.layout}
      style={{ width }}
      layout={layout}
      compactor={dashboardGridCompactor}
      dragConfig={{ enabled: true }}
      resizeConfig={{
        enabled: true,
        handles: [resizeHandleAxis],
        handleComponent: renderResizeHandle,
      }}
      gridConfig={{
        cols: columns,
        rowHeight: DASHBOARD_GRID_STEP,
        containerPadding: DASHBOARD_GRID_CONTAINER_PADDING,
        margin: DASHBOARD_GRID_MARGIN,
      }}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
    >
      {renderWidgetItems(widgets, isRTL, true, onRemoveWidget)}
    </ReactGridLayout>
  )
}

interface ReadOnlyDashboardGridProps {
  widgets: Widget[]
  isRTL: boolean
  isMobile: boolean
  containerWidth: number
}

const ReadOnlyDashboardGrid: FC<ReadOnlyDashboardGridProps> = ({
  widgets,
  isRTL,
  isMobile,
  containerWidth,
}) => {
  const layout = useMemo(
    () =>
      widgets.flatMap((widget) => {
        const widgetLayout = isMobile ? widget.mobileLayout : widget.layout

        return widgetLayout ? [widgetLayout] : []
      }),
    [isMobile, widgets]
  )
  const width = isMobile ? containerWidth : Math.min(containerWidth, DASHBOARD_GRID_MAX_WIDTH)

  return (
    <ReactGridLayout
      width={width}
      className={styles.layout}
      style={{ width }}
      layout={layout}
      compactor={noCompactor}
      dragConfig={{ enabled: false }}
      resizeConfig={{ enabled: false }}
      gridConfig={{
        cols: DASHBOARD_GRID_COLUMNS,
        rowHeight: DASHBOARD_GRID_STEP,
        containerPadding: DASHBOARD_GRID_CONTAINER_PADDING,
        margin: DASHBOARD_GRID_MARGIN,
      }}
    >
      {renderWidgetItems(widgets, isRTL, false)}
    </ReactGridLayout>
  )
}

function getEditableWidgetLayout(widget: Widget): WidgetLayout | null {
  if (!widget.layout) {
    return null
  }

  return getWidgetConstraints(widget.layout, widget.id, widget.layout, widget.size)
}

function renderWidgetItems(
  widgets: Widget[],
  isRTL: boolean,
  isEditMode: boolean,
  onRemoveWidget?: (type: WidgetType) => void
): ReactElement[] {
  return widgets.map((widget) => (
    <div key={widget.id} className={styles.widgetWrapper}>
      <div
        className={classNames(styles.widgetContent, {
          [styles.widgetRTL]: isRTL,
        })}
      >
        {isEditMode && onRemoveWidget ? (
          <WidgetContainer widget={widget} isEditMode onRemove={onRemoveWidget} />
        ) : (
          <WidgetContainer widget={widget} />
        )}
      </div>
    </div>
  ))
}

function renderResizeHandle(axis: ResizeHandleAxis, ref: Ref<HTMLElement>) {
  return (
    <div
      ref={ref as Ref<HTMLDivElement>}
      className={classNames('react-resizable-handle', 'react-resizable-handle-' + axis)}
    >
      <IconButton.Secondary aria-label='Resize widget' size='sm' icon={ResizeIcon} />
    </div>
  )
}
